import type { CSSProperties } from "react";
import { StoreApi, UseBoundStore } from "zustand";

import { IObjectWithTypename, NodeStore } from "@/components/@types";
import { FieldCell } from "@/components/organisms/Field/FieldCell";
import { Values } from "@/constants";
import { CellProps } from "@/hooks/useContextTree";
import { ObjectFieldDefinitionType } from "@/objects/@types";
import { cn } from "@/components/ui/utils";

export function NodeValueCell({
  fieldDefinition,
  data,
  store,
  fieldEvents,
  isEditable,
  isLoading,
  isMultiline,
  isSingleClick,
  size,
  cellProps,
  cellStyles,
  cellClassName,
  variant,
  fieldVariant = "node",
  deltaMode,
  cellBackground,
  isLastColumn,
  hasDelta,
  isRowDimmed,
  changeVariant,
}: {
  fieldDefinition: ObjectFieldDefinitionType<IObjectWithTypename>;
  data: Record<string, unknown>;
  store: UseBoundStore<StoreApi<NodeStore>>;
  fieldEvents?: Record<string, unknown>;
  isEditable: boolean;
  isLoading: boolean;
  isMultiline: boolean;
  isSingleClick: boolean;
  size: "sm" | "md";
  cellProps?: CellProps;
  /** The cell's paddings and height, and what marks it, as CSS. */
  cellStyles: CSSProperties;
  /** A ground marking it, which its hover still changes. */
  cellClassName?: string;
  fieldVariant?: "node" | "cell";
  variant?: "normal" | "before" | "after";
  deltaMode?: boolean;
  cellBackground?: string;
  isLastColumn?: boolean;
  /** When true and last column, show orange bar on right (delta row indicator) */
  hasDelta?: boolean;
  /** When true, dim only the content (opacity) for row dimming; cell background unchanged */
  isRowDimmed?: boolean;
  /** Hint about the kind of change: "create" → green indicator bar */
  changeVariant?: "create" | "update";
}) {
  const resolvedCellBackground = cellBackground ?? "var(--card)";
  const rowHeight =
    size === "md" ? Values.DEFAULT_ROW_HEIGHT : Values.COMPACT_ROW_HEIGHT;

  // In the key order Joy's ``sx`` wrote them: a shorthand after its longhands
  // still resets them, an ``outline`` replaced in place still comes before the
  // ``outlineColor`` after it.
  const fieldCellStyle: CSSProperties = {
    position: "relative",
    transition: deltaMode ? "none" : "outline 0.1s ease-in-out",
    outline: deltaMode ? "none" : "1px solid transparent",
    outlineOffset: "-1px",
    ...cellStyles,
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    ...(deltaMode && {
      paddingTop: 0,
      paddingBottom: 0,
      margin: 0,
      border: "none",
      minHeight: rowHeight,
      display: "flex",
      ...(variant === "before" && hasDelta && { opacity: 0.7 }),
    }),
    ...(fieldDefinition.type === "table" && { padding: 0 }),
    ...(!deltaMode &&
      variant === "before" && {
        outline: "1px solid",
        outlineColor: "var(--foreground)",
        opacity: 0.7,
        textDecoration: "line-through",
      }),
    ...(!deltaMode &&
      variant === "after" && {
        outline: "1px solid",
        outlineColor: "var(--muted-foreground)",
      }),
  };
  // What its hover and focus change, or what a style cannot say.
  const fieldCellClassName = cn(
    // As a delta's cell, its ground clear over any marking it (Joy's
    // ``background`` came after the mark's ``backgroundColor``).
    deltaMode ? "[background:transparent] [box-shadow:none]" : cellClassName,
    deltaMode &&
      variant === "before" &&
      hasDelta &&
      "[&_*]:line-through! [&_input]:line-through!",
    fieldDefinition.type === "table" &&
      !deltaMode &&
      "[outline:1px_solid_var(--border)]!",
    !deltaMode && variant === "before" && "bg-secondary",
    !deltaMode && variant === "after" && "bg-secondary",
  );

  const fieldCell = (
    <FieldCell
      type={fieldDefinition.type}
      getValue={fieldDefinition.getValue}
      getValueOptions={fieldDefinition?.getValueOptions}
      data={data}
      isEditable={isEditable}
      store={store}
      fieldEvents={fieldEvents}
      size={size}
      isMultiline={isMultiline}
      isLoading={isLoading}
      isSingleClick={isSingleClick}
      fieldVariant={fieldVariant}
      hoverBackgroundColor={deltaMode && !isLastColumn ? null : undefined}
      style={{
        textAlign: "left",
      }}
      slotProps={{
        cell: {
          ...cellProps,
          ...(cellProps && {
            focusOutlineColor: "var(--foreground)",
            // focusBoxShadow: "var(--shadow-sm)",
          }),
          className: cn(cellProps?.className, fieldCellClassName),
          style: fieldCellStyle,
          radius: deltaMode
            ? "0px"
            : fieldDefinition.type === "table"
              ? "calc(var(--radius) - 2px)"
              : size === "md"
                ? "calc(var(--radius) - 4px)"
                : "0.25rem",
          ...(deltaMode && { height: `${rowHeight}px` }),
        },
      }}
    />
  );

  return (
    <div
      className={cn(
        "pt-0 pb-0 w-full min-w-0 flex items-center",
        deltaMode
          ? "pl-0 bg-(--cell-background) min-h-(--node-row-height) h-(--node-row-height)"
          : size === "sm"
            ? "pl-1.5"
            : "pl-2",
      )}
      style={
        deltaMode
          ? ({
              "--cell-background": resolvedCellBackground,
              "--node-row-height": `${rowHeight}px`,
            } as CSSProperties)
          : undefined
      }
    >
      {deltaMode && isRowDimmed ? (
        <div
          data-dimmed-content
          className="opacity-[0.35] filter-[saturate(0)] min-w-0 min-h-full flex items-center w-full"
        >
          {fieldCell}
        </div>
      ) : (
        fieldCell
      )}
    </div>
  );
}
